import { useState } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';

const FAQS = [
  {
    q: '免费51漫画上的漫画都可以免费看吗？',
    a: '是的，站内大部分作品都支持免费在线观看。你可以按热血类、搞笑类、少女漫、少年漫、完结作品、汉化组等标签筛选找到想看的漫画，直接进入详情页阅读。',
  },
  {
    q: '如何快速找到我想看的漫画类型？',
    a: '首页提供六大类型标签，点击对应标签即可进入该分类页面查看全部作品。你也可以使用顶部搜索框输入漫画名、作者或类型关键词快速定位。',
  },
  {
    q: '连载中的漫画多久更新一次？',
    a: '不同作品更新频率不同，多数连载漫画每周更新 1-2 话，部分热门作品每日更新。请以作品详情页展示的「更新频率」与「最新章节」为准。',
  },
  {
    q: '完结作品和连载中作品有什么区别？',
    a: '完结作品表示全部章节已更新完毕，可以一次性完整阅读；连载中作品仍在持续更新，适合喜欢追更体验的读者。',
  },
  {
    q: '什么是汉化组专区？',
    a: '汉化组专区收录了由优质汉化组精校翻译的作品，翻译质量与校对水准更有保障，方便喜欢优质翻译的读者集中浏览。',
  },
  {
    q: '追更的漫画会不会突然中断？',
    a: '我们会尽量保证更新稳定。若某部作品因汉化组排期或版权原因暂时停更，会在作品详情页标注说明，避免读者空等。',
  },
  {
    q: '如何收藏或追更喜欢的漫画？',
    a: '进入作品详情页后，点击「追更」或「收藏」按钮即可标记喜欢的内容。目前为站内记录功能，后续接入账户系统后可同步到云端。',
  },
  {
    q: '遇到无法播放或图片加载失败怎么办？',
    a: '建议先刷新页面并检查网络。若问题持续存在，可通过页脚邮箱联系我们的客服团队，我们会尽快协助处理。',
  },
  {
    q: '内容涉嫌侵权如何处理？',
    a: '我们尊重创作者与版权方，坚持支持正版。如发现涉嫌侵权的内容，请通过 contact@free51manga.example 与我们联系，我们会在核实后及时处理。',
  },
];

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number>(0);

  usePageMeta({
    title: '常见问题 FAQ - 免费51漫画',
    description:
      '免费51漫画常见问题解答：如何免费看漫画、如何按热血类、搞笑类、少女漫、少年漫、完结作品、汉化组标签筛选、更新频率与追更相关疑问。',
    path: '/faq',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shonenx.github.io/' },
          { '@type': 'ListItem', position: 2, name: '常见问题', item: 'https://shonenx.github.io/faq' },
        ],
      },
    ],
  });

  return (
    <main className="w-full bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 text-center md:px-6 md:py-16">
          <nav className="text-sm text-foreground-600" aria-label="面包屑">
            <ol className="flex items-center justify-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li aria-current="page" className="font-medium text-foreground-900">常见问题</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            常见问题
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm text-foreground-600 md:text-base">
            关于免费观看、标签筛选、更新频率与追更的常见疑问，这里都有答案。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
        <div className="space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={f.q}
                className="overflow-hidden rounded-lg border border-background-200 bg-background-100"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm ${
                        isOpen ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-primary-600'
                      }`}
                    >
                      <i className={isOpen ? 'ri-question-fill' : 'ri-question-line'}></i>
                    </span>
                    <span className="text-sm font-bold text-foreground-900 md:text-base">{f.q}</span>
                  </span>
                  <i
                    className={`shrink-0 text-lg transition-transform duration-300 ${
                      isOpen ? 'rotate-180 text-primary-600' : 'text-foreground-600'
                    } ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`}
                  ></i>
                </button>
                {isOpen && (
                  <div className="border-t border-background-200 px-5 py-4">
                    <p className="text-sm leading-relaxed text-foreground-700">{f.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 rounded-lg bg-background-100 p-6 text-center">
          <p className="text-sm text-foreground-700">还有更多疑问？</p>
          <Link
            to="/about"
            className="mt-3 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            联系我们
            <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
      </section>
    </main>
  );
}